import {
  Boxes,
  Building2,
  CalendarDays,
  FolderKanban,
  MapPin,
  Users,
  Package,
  Puzzle,
  Quote,
  Truck,
  User,
  Wrench,
  type LucideIcon,
} from "lucide-react";
import type { EntityType } from "./api";

/**
 * The twelve entity types (UI-6; internal product and feature since 0072), each with the icon it wears wherever it appears and the
 * words the manager and navigator use for it.
 *
 * One place, so a type reads the same in the table row, the detail header, the add dialog
 * and the navigator's "By type" section. `order` is the handoff's: persons first, then the
 * rest as the feature lists them — and `term` last, because it is the one that is not a
 * thing in the world but a word for one, and because it is the newest.
 */
export interface EntityTypeMeta {
  type: EntityType;
  /** Singular, for the detail header's type badge and the add dialog. */
  label: string;
  /** Plural, for the navigator's "By type" rows. */
  plural: string;
  icon: LucideIcon;
}

export const ENTITY_TYPES: EntityTypeMeta[] = [
  { type: "person", label: "Person", plural: "Persons", icon: User },
  { type: "product", label: "Product", plural: "Products", icon: Package },
  { type: "internal_product", label: "Internal product", plural: "Internal products", icon: Boxes },
  { type: "service", label: "Service", plural: "Services", icon: Wrench },
  { type: "feature", label: "Feature", plural: "Features", icon: Puzzle },
  { type: "customer", label: "Customer", plural: "Customers", icon: Building2 },
  { type: "supplier", label: "Supplier", plural: "Suppliers", icon: Truck },
  { type: "location", label: "Location", plural: "Locations", icon: MapPin },
  { type: "event", label: "Event", plural: "Events", icon: CalendarDays },
  { type: "initiative", label: "Initiative", plural: "Initiatives", icon: FolderKanban },
  { type: "team", label: "Team", plural: "Teams", icon: Users },
  { type: "term", label: "Term", plural: "Terms", icon: Quote },
];

const BY_TYPE = new Map(ENTITY_TYPES.map((meta) => [meta.type, meta]));

export function entityMeta(type: EntityType): EntityTypeMeta {
  return BY_TYPE.get(type) ?? ENTITY_TYPES[0]!;
}

export function entityIcon(type: EntityType): LucideIcon {
  return entityMeta(type).icon;
}

export function entityTypeLabel(type: EntityType): string {
  return entityMeta(type).label;
}
